import { Body, Controller, Get, Post, Query } from '@nestjs/common';
import { AuthService } from './auth.service';
import { SigninDto, SignupDto } from '@crm/dto';
import { ApiResponseInterface } from '@crm/interface';
import { IsEmail, IsString, MinLength } from 'class-validator';

class ForgotPasswordRequestDto {
  @IsEmail()
  email!: string;

  @IsString()
  redirectTo!: string;
}

class ForgotPasswordResetDto {
  @IsString()
  access_token!: string;

  @IsString()
  @MinLength(8)
  new_password!: string;
}

class ResendConfirmationDto {
  @IsEmail()
  email!: string;
}

@Controller('auth')
export class AuthController {
  constructor(private readonly authService: AuthService) {}

  /**
   * POST /auth/signup
   * Body: { email, password }
   */
  @Post('signup')
  async signup(@Body() dto: SignupDto): Promise<ApiResponseInterface> {
    return this.authService.signup(dto);
  }

  /**
   * POST /auth/signin
   * Body: { email, password }
   */
  @Post('signin')
  async signin(@Body() dto: SigninDto): Promise<ApiResponseInterface> {
    return this.authService.signin(dto);
  }

  /**
   * GET /auth/oauth/google
   * Query param: redirectUrl (your frontend callback URL)
   */
  @Get('oauth/google')
  async signinWithOAuth(
    @Query('redirectUrl') redirectUrl: string,
  ): Promise<ApiResponseInterface> {
    return this.authService.signinWithOAth(redirectUrl);
  }

  /**
   * POST /auth/signout
   */
  @Post('signout')
  async signout(): Promise<ApiResponseInterface> {
    return this.authService.signout();
  }

  /**
   * POST /auth/forgot-password/request
   * Body: { email, redirectTo }
   */
  @Post('forgot-password/request')
  async forgotPasswordRequest(
    @Body() dto: ForgotPasswordRequestDto,
  ): Promise<ApiResponseInterface> {
    return this.authService.forgotPasswordRequest(dto.email, dto.redirectTo);
  }

  /**
   * POST /auth/forgot-password/reset
   * Body: { access_token, new_password }
   */
  @Post('forgot-password/reset')
  async forgotPasswordReset(
    @Body() dto: ForgotPasswordResetDto,
  ): Promise<ApiResponseInterface> {
    return this.authService.forgotPasswordReset(dto.access_token, dto.new_password);
  }

  /**
   * POST /auth/resend-confirmation
   * Body: { email }
   */
  @Post('resend-confirmation')
  async resendConfirmation(
    @Body() dto: ResendConfirmationDto,
  ): Promise<ApiResponseInterface> {
    return this.authService.resendConfirmation(dto.email);
  }
}